import Vue from 'vue'
import Router from 'vue-router'
import Meta from 'vue-meta'
import multiguard from 'vue-router-multiguard'
import Auth from '../services/auth'
import { getSubdomain } from '@/services/utils'
import { getPageTitleAndPath, pagesToIgnore } from '@/services/googleAnalytics'

import Home from '../views/Home.vue'
const Error404 = () => import('@/views/Error404')
const Verification = () => import('../views/Verification')
const Registration = () => import('../views/Registration')
const Login = () => import('../views/Login')
const TwoFactorAuth = () => import('../views/TwoFactorAuth')
const HouseholdDetailsOnboarding = () => import('../views/surveys/HouseholdDetailsOnboarding')
const FinancialHealthOnboarding = () => import('../views/surveys/FinancialHealthOnboarding')
const SurveyResponseDetail = () => import('@/views/surveys/SurveyResponseDetail')
const Request = () => import('../views/Request')
const DonationSummary = () => import('../views/donations/DonationSummary')
const AddPayment = () => import('../views/donations/AddPayment')
const MyActivity = () => import('../views/MyActivity')
const Messages = () => import('../views/Messages')
const MyProfile = () => import('../views/MyProfile')
const FAQ = () => import('../views/FAQ')
const DonationReview = () => import('@/views/donations/DonationReview')
const PayrollReview = () => import('@/views/donations/PayrollReview')
const PayrollSummary = () => import('@/views/donations/PayrollSummary')
const PayPalVerify = () => import('../views/PayPalVerify')
const EventCategories = () => import('../views/requests/EventCategories')
const ExpenseCategories = () => import('../views/requests/ExpenseCategories')
const CreateEvents = () => import('../views/requests/CreateEvents')
const ExpenseDocumentation = () => import('../views/requests/ExpenseDocumentation')
const EventDocumentation = () => import('../views/requests/EventDocumentation')
const RequestSummary = () => import('../views/requests/RequestSummary')
const RequestSubmitted = () => import('@/views/requests/RequestSubmitted')
const PasswordResetRequest = () => import('@/views/PasswordResetRequest')
const EventWarning = () => import('../views/requests/EventWarning')
const ExpenseWarning = () => import('../views/requests/ExpenseWarning')
const PasswordResetConfirm = () => import('@/views/PasswordResetConfirm')
const HouseholdDetailsRequest = () => import('../views/requests/surveys/HouseholdDetailsRequest')
const FinancialHealthRequest = () => import('../views/requests/surveys/FinancialHealthRequest')
const FindResources = () => import('@/views/FindResources')
const Validation = () => import('../views/Validation')

// Staff Portal Views
const RequestsOverview = () => import('@/views/staff/RequestsOverview')
const RequestDetail = () => import('@/views/staff/RequestDetail')
const PayoutsOverview = () => import('@/views/staff/PayoutsOverview')
const EmployeesOverview = () => import('@/views/staff/EmployeesOverview')
const EmployeeDetail = () => import('@/views/staff/EmployeeDetail')
const Reports = () => import('@/views/staff/Reports')
const EligibilityUploader = () => import('@/views/staff/EligibilityUploader')
const EligibilityFileFeed = () => import('@/views/staff/EligibilityFileFeed')
const Uploader = () => import('@/views/staff/Uploader')

Vue.use(Router)
Vue.use(Meta)

const router = new Router({
  // This mode removes trailing /#/ sign from URLs
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return { selector: to.hash }
    }
    if (savedPosition) {
      return savedPosition
    }
    return { x: 0, y: 0 }
  },

  routes: [
    // OPEN/UNGUARDED VIEWS
    {
      path: '/',
      name: 'Home',
      component: Home,
      props: true,
    },
    {
      path: '/error404',
      name: 'Error404',
      component: Error404,
    },
    {
      path: '/verification',
      name: 'Verification',
      component: Verification,
    },
    {
      path: '/signup',
      name: 'Registration',
      component: Registration,
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
    },
    {
      path: '/2fa',
      name: 'TwoFactorAuth',
      component: TwoFactorAuth,
      beforeEnter: Auth.requireNoAuth(),
    },
    {
      path: '/survey-responses/:id',
      name: 'SurveyResponseDetail',
      component: SurveyResponseDetail,
    },
    {
      path: '/faq',
      name: 'FAQ',
      component: FAQ,
    },
    {
      path: '/password/reset',
      name: 'PasswordResetRequest',
      component: PasswordResetRequest,
    },
    {
      path: '/password/reset/confirm/:uid/:token',
      name: 'PasswordResetConfirm',
      component: PasswordResetConfirm,
    },

    // Donation screens are available to anonymous users
    {
      path: '/add-payment',
      name: 'AddPayment',
      component: AddPayment,
      beforeEnter: Auth.requireDonationsEnabled(),
    },
    {
      path: '/donation-review',
      name: 'DonationReview',
      component: DonationReview,
      beforeEnter: Auth.requireDonationsEnabled(),
    },
    {
      path: '/donation-summary',
      name: 'DonationSummary',
      component: DonationSummary,
    },
    {
      path: '/donation-summary/:id',
      name: 'DonationSummary',
      component: DonationSummary,
    },
    {
      path: '/payroll-review',
      name: 'PayrollReview',
      component: PayrollReview,
      beforeEnter: Auth.requireDonationsEnabled(),
    },
    {
      path: '/payroll-summary',
      name: 'PayrollSummary',
      component: PayrollSummary,
    },
    {
      path: '/payroll-summary/:id',
      name: 'PayrollSummary',
      component: PayrollSummary,
    },
    {
      path: '/resources',
      name: 'FindResources',
      component: FindResources,
      beforeEnter: Auth.requireSpringFourIntegration(),
    },

    // EMPLOYEE ONLY VIEWS
    {
      path: '/validation',
      name: 'Validation',
      component: Validation,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
      props: true,
    },
    {
      path: '/household-details-survey',
      name: 'HouseholdDetailsOnboarding',
      component: HouseholdDetailsOnboarding,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/financial-health-survey',
      name: 'FinancialHealthOnboarding',
      component: FinancialHealthOnboarding,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/start-request',
      name: 'Request',
      component: Request,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/request-household-details',
      name: 'HouseholdDetailsRequest',
      component: HouseholdDetailsRequest,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/request-financial-health',
      name: 'FinancialHealthRequest',
      component: FinancialHealthRequest,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/my-activity',
      name: 'MyActivity',
      component: MyActivity,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/messages',
      name: 'Messages',
      component: Messages,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/my-profile',
      name: 'MyProfile',
      component: MyProfile,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/pay-pal-verify',
      name: 'PayPalVerify',
      component: PayPalVerify,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/event-categories',
      name: 'EventCategories',
      component: EventCategories,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/expense-categories',
      name: 'ExpenseCategories',
      component: ExpenseCategories,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/create-events',
      name: 'CreateEvents',
      component: CreateEvents,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/expense-documentation',
      name: 'ExpenseDocumentation',
      component: ExpenseDocumentation,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/event-documentation',
      name: 'EventDocumentation',
      component: EventDocumentation,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/event-warning',
      name: 'EventWarning',
      component: EventWarning,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/expense-warning',
      name: 'ExpenseWarning',
      component: ExpenseWarning,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/request-summary',
      name: 'RequestSummary',
      component: RequestSummary,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/request-submitted',
      name: 'RequestSubmitted',
      component: RequestSubmitted,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyEmployees: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },

    // STAFF/ADMIN ONLY VIEWS
    {
      path: '/staff/reports',
      name: 'StaffReports',
      component: Reports,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyStaffOrAdmins: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/staff/requests',
      name: 'StaffRequestsOverview',
      component: RequestsOverview,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyStaffOrAdmins: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/staff/requests/:id',
      name: 'StaffRequestDetail',
      component: RequestDetail,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyStaffOrAdmins: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/staff/disbursements',
      name: 'StaffPayoutsOverview',
      component: PayoutsOverview,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyStaffOrAdmins: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/staff/employees',
      name: 'StaffEmployeesOverview',
      component: EmployeesOverview,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyStaffOrAdmins: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/staff/employees/:id',
      name: 'StaffEmployeeDetail',
      component: EmployeeDetail,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyStaffOrAdmins: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/staff/eligibility-uploader',
      name: 'StaffEligibilityUploader',
      component: EligibilityUploader,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyStaffOrAdmins: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/staff/eligibility-file-feed',
      name: 'StaffEligibilityFileFeed',
      component: EligibilityFileFeed,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyStaffOrAdmins: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    {
      path: '/staff/uploads',
      name: 'Uploader',
      component: Uploader,
      beforeEnter: Auth.requireAuthAndPermissions({ onlyStaffOrAdmins: true }),
      meta: {
        validateTokenOnRefresh: true,
      },
    },
    { path: '*', redirect: '/error404' },
  ],
})

/**
 * The below @afterEach is a navigation-guard provided by Vue Router.
 * After each route change, the callback provided will run:
 * pinging Google with the pageview,
 * allowing for pageview tracking via Google Analytics.
 * TODO(Bruno 03-26-20) I would love to abstract this method but it depends on $router for $gtag + $store,
 * and using Vue.prototype to access $store in /services/googleAnalytics yields undefined
 **/
router.afterEach(function(to) {
  if (pagesToIgnore[to.name]) {
    return
  }
  let {
    $gtag,
    $store: {
      getters: { userIsLoggedIn },
    },
  } = router.app
  let { pageTitle, pagePath } = getPageTitleAndPath(to, userIsLoggedIn)
  $gtag.query('set', 'dimension1', getSubdomain())
  $gtag.query('set', 'dimension2', pagePath)
  $gtag.pageview({
    page_title: pageTitle,
    page_path: pagePath,
  })
})

export default router
